import { API_HOSTNAME, WEBSOCKET_HOSTNAME } from '@/config';

const LOCAL_API_TO_WS_PORT: Record<string, string> = {
  '3000': '3002',
  '3010': '3011',
};

// Global hostname manager for both API and WebSocket endpoints
class HostnameManager {
  private currentApiHostname: string;
  private currentWebSocketHostname: string;

  constructor() {
    // Initialize with local self-hosted defaults.
    this.currentApiHostname = API_HOSTNAME ?? 'http://localhost:3000';
    this.currentWebSocketHostname = WEBSOCKET_HOSTNAME ?? 'ws://localhost:3002';
  }

  setApiHostname(hostname: string) {
    const normalizedHostname = hostname.replace(/\/+$/, '');
    const previousWebSocketFromApi = this.getLocalWebSocketForApi(this.currentApiHostname);
    const nextWebSocketFromApi = this.getLocalWebSocketForApi(normalizedHostname);

    this.currentApiHostname = normalizedHostname;

    /**
     * Keep WS aligned only when the existing WS value is still the default pair for the old API
     * host (so we do not override explicit user/proxy configuration).
     */
    if (
      previousWebSocketFromApi &&
      nextWebSocketFromApi &&
      this.currentWebSocketHostname.replace(/\/+$/, '') === previousWebSocketFromApi
    ) {
      this.currentWebSocketHostname = nextWebSocketFromApi;
    }
  }

  getApiHostname(): string {
    return this.currentApiHostname;
  }

  setWebSocketHostname(hostname: string) {
    this.currentWebSocketHostname = hostname;
  }

  getWebSocketHostname(): string {
    return this.currentWebSocketHostname;
  }

  // Convenience methods for backward compatibility
  setHostname(hostname: string) {
    this.setApiHostname(hostname);
  }

  getHostname(): string {
    return this.getApiHostname();
  }

  private getLocalWebSocketForApi(apiHostname: string): string | undefined {
    try {
      const parsed = new URL(apiHostname);
      const isLocalHost =
        parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1' || parsed.hostname === '::1';
      if (!isLocalHost) {
        return undefined;
      }

      const apiPort = parsed.port || (parsed.protocol === 'https:' ? '443' : '80');
      const mappedWebSocketPort = LOCAL_API_TO_WS_PORT[apiPort];
      if (!mappedWebSocketPort) {
        return undefined;
      }

      const wsProtocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';

      return `${wsProtocol}//${parsed.hostname}:${mappedWebSocketPort}`;
    } catch {
      return undefined;
    }
  }
}

export const apiHostnameManager = new HostnameManager();
